import {
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { makeStyles } from '@material-ui/styles';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory, useLocation, useParams } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';
import matchingRoundsAPI, {
  MatchingRound,
  MatchingRoundDetails,
  MatchingRoundId,
  MatchingRoundStatus,
} from '../../api/matching-rounds';
import {
  Button,
  ConfirmDialog,
  PageLoader,
  SnackMessage,
} from '../../components/common';
import { ActionsDialog } from '../../components/common/Confirmation/ActionsDialog';
import BaseLayout from '../../components/layout/base-layout';
import { Header } from '../../components/layout/main-layout';
import MatchingRoundPageTabs from '../../components/matching-rounds/page-tabs';
import { VentureSubgroupSelectorHandle } from '../../components/matching-rounds/venture-subgroup-selector';
import { useResourceBundles } from '../../contexts/resource-bundles-context';
import { UserContext } from '../../contexts/user-context';
import { getRoutePath, Pages } from '../../router/constants';
import { ProtectedRouteProps } from '../../router/type';
import MatchingRoundDetailsInfoPage from './matching-round-details-info-page';
import MatchingRoundHeatmapPage from './matching-round-heatmap-page';
import MatchingRoundLogsPage from './matching-round-logs-page';
import MatchingRoundSubgroupsPage from './matching-round-subgroups-page';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    padding: 0,
    overflow: 'hidden',
    // 232px = header(56) + padding(40) + page-header(68) + gap(24) + footer(20) + paddingBottom(24)
    maxHeight: 'calc(100vh - 232px)',
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    overflow: 'hidden',
    minHeight: 0,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    padding: '0 40px 24px',
    flexShrink: 0,
  },
});

function MatchingRoundDetailsPage({ user }: ProtectedRouteProps) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { hasAccessToAction } = useContext(UserContext);
  const { rb } = useResourceBundles();
  const history = useHistory();
  const { roundId } = useParams<{ roundId?: string }>();
  const location = useLocation<{ createdRound?: MatchingRound }>();

  const isEditMode = !!roundId;
  const createdRound = location.state?.createdRound ?? null;

  const [isSaving, setIsSaving] = useState(false);
  const [isSimulating, setIsSimulating] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isLoadingRound, setIsLoadingRound] = useState(
    isEditMode && !createdRound,
  );
  const [existingRound, setExistingRound] = useState<MatchingRound | null>(
    createdRound,
  );
  const [heatmapRefreshKey, setHeatmapRefreshKey] = useState(0);
  const [isReviewConfirmOpen, setIsReviewConfirmOpen] = useState(false);
  const [isReviewSuccessOpen, setIsReviewSuccessOpen] = useState(false);
  const [isSendingReview, setIsSendingReview] = useState(false);

  const selectorRef = useRef<VentureSubgroupSelectorHandle>(null);

  useEffect(() => {
    if (!isEditMode) return;
    // Skip fetch if round was passed via route state (just created)
    if (createdRound) return;

    const loadRound = async () => {
      try {
        setIsLoadingRound(true);
        const rounds = await matchingRoundsAPI.getAll();
        const found = rounds.find((r) => r.id === roundId);
        if (found) {
          setExistingRound(found);
        } else {
          enqueueSnackbar('Matching round not found', {
            variant: 'error',
          });
          history.push(Pages.MATCHING_ROUNDS);
        }
      } catch (e: unknown) {
        const messageError = (e as AxiosError)?.response?.data?.message;
        enqueueSnackbar('An error occurred while loading the matching round.', {
          content: (key, message) =>
            SnackMessage({
              key,
              message,
              variant: 'error',
              additionalMessage: messageError,
            }),
          variant: 'error',
        });
        history.push(Pages.MATCHING_ROUNDS);
      } finally {
        setIsLoadingRound(false);
      }
    };

    loadRound();
  }, [isEditMode, roundId, enqueueSnackbar, history, createdRound]);

  const handleSubmit = useCallback(
    async (values: MatchingRoundDetails) => {
      try {
        setIsSaving(true);

        if (isEditMode && existingRound) {
          await matchingRoundsAPI.update({
            ...existingRound,
            ...values,
          });

          setExistingRound({ ...existingRound, ...values });
          enqueueSnackbar('The matching round was successfully updated', {
            variant: 'success',
          });
        } else {
          const created = await matchingRoundsAPI.create(values);
          enqueueSnackbar('The matching round was successfully created', {
            variant: 'success',
          });
          history.replace({
            pathname: getRoutePath(Pages.MATCHING_ROUND_SUBGROUPS, {
              roundId: created.id,
            }),
            state: { createdRound: created },
          });
        }
      } catch (e: unknown) {
        const messageError = (e as AxiosError)?.response?.data?.message;
        enqueueSnackbar(
          `An error occurred while ${
            isEditMode ? 'updating' : 'creating'
          } the matching round. Please, try again.`,
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
      } finally {
        setIsSaving(false);
      }
    },
    [isEditMode, existingRound, enqueueSnackbar, history],
  );

  const executeSimulation = useCallback(async () => {
    if (!existingRound) return;

    try {
      setIsSimulating(true);

      await matchingRoundsAPI.simulateMatches(existingRound.id);

      // Update status to PLANNED
      try {
        const updatedRound = {
          ...existingRound,
          status: MatchingRoundStatus.PLANNED,
        };
        await matchingRoundsAPI.update(updatedRound);
        setExistingRound(updatedRound);
      } catch (statusError: unknown) {
        const statusMsg = (statusError as AxiosError)?.response?.data?.message;
        enqueueSnackbar(
          'Simulation completed, but status update failed. Please update the status manually.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: statusMsg,
              }),
            variant: 'error',
          },
        );
        return;
      }

      setHeatmapRefreshKey((k) => k + 1);
      history.push(getRoutePath(Pages.MATCHING_ROUND_HEATMAP, { roundId }));
      enqueueSnackbar('Simulation completed successfully', {
        variant: 'success',
      });
    } catch (e: unknown) {
      const messageError = (e as AxiosError)?.response?.data?.message;
      enqueueSnackbar('An error occurred while simulating matches.', {
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: messageError,
          }),
        variant: 'error',
      });
    } finally {
      setIsSimulating(false);
    }
  }, [existingRound, enqueueSnackbar, history, roundId]);

  const handleSimulate = useCallback(() => {
    if (!existingRound) return;

    // Only check venture count when selector is mounted (Subgroups tab)
    if (selectorRef.current) {
      const selectedCount = selectorRef.current.getSelectedCount();
      if (selectedCount === 0) {
        enqueueSnackbar(
          'We can simulate a round of assignments only when at least 1 Venture is selected',
          { variant: 'warning' },
        );
        return;
      }
    }

    if (existingRound.status !== MatchingRoundStatus.CREATED) {
      setIsConfirmOpen(true);
      return;
    }

    executeSimulation();
  }, [existingRound, enqueueSnackbar, executeSimulation]);

  const handleConfirmSimulate = useCallback(() => {
    setIsConfirmOpen(false);
    executeSimulation();
  }, [executeSimulation]);

  const handleReview = useCallback(() => {
    setIsReviewConfirmOpen(true);
  }, []);

  const handleConfirmReview = useCallback(async () => {
    if (!existingRound) return;
    setIsReviewConfirmOpen(false);
    try {
      setIsSendingReview(true);
      await matchingRoundsAPI.sendApprovals(existingRound.id);
      setExistingRound({ ...existingRound, isApprovalRequestSent: true });
      setIsReviewSuccessOpen(true);
    } catch (e: unknown) {
      const messageError = (e as AxiosError)?.response?.data?.message;
      enqueueSnackbar('An error occurred while sending approval requests.', {
        content: (key, message) =>
          SnackMessage({
            key,
            message,
            variant: 'error',
            additionalMessage: messageError,
          }),
        variant: 'error',
      });
    } finally {
      setIsSendingReview(false);
    }
  }, [existingRound, enqueueSnackbar]);

  const title = isEditMode
    ? existingRound?.title || 'Edit Matching Round'
    : 'New Matching Round';

  const isNotCreated =
    !!existingRound && existingRound.status !== MatchingRoundStatus.CREATED;

  const showSimulate =
    isEditMode && hasAccessToAction('matchingRound.details.update');

  const showHeatmapActions =
    isNotCreated && hasAccessToAction('matchingRound.details.update');

  const initialFormValues = useMemo(
    () =>
      existingRound
        ? {
            title: existingRound.title,
            creationDate: existingRound.creationDate,
            description: existingRound.description,
            status: existingRound.status,
          }
        : undefined,
    [existingRound],
  );

  const typedRoundId = isEditMode ? (roundId as MatchingRoundId) : undefined;

  return (
    <BaseLayout user={user} fullHeight>
      {isLoadingRound ? (
        <PageLoader />
      ) : (
        <>
          <Header
            title={title}
            backLink={
              <Tooltip title='Back to Matching Rounds'>
                <IconButton
                  onClick={() => history.goBack()}
                  data-testid='button-back-matching-rounds'
                >
                  <ArrowBackIcon />
                </IconButton>
              </Tooltip>
            }
          />
          <Paper className={classes.container} elevation={1}>
            <MatchingRoundPageTabs
              roundId={typedRoundId}
              roundStatus={existingRound?.status}
            />
            <div className={classes.content}>
              <Switch>
                <Route
                  exact
                  path={Pages.MATCHING_ROUND_DETAILS}
                  render={() => (
                    <MatchingRoundDetailsInfoPage
                      loading={isSaving}
                      onSubmit={handleSubmit}
                      initialValues={initialFormValues}
                    />
                  )}
                />
                {isEditMode && (
                  <>
                    <Route
                      exact
                      path={Pages.MATCHING_ROUND_SUBGROUPS}
                      render={() => (
                        <MatchingRoundSubgroupsPage
                          roundId={typedRoundId!}
                          selectorRef={selectorRef}
                        />
                      )}
                    />
                    <Route
                      exact
                      path={Pages.MATCHING_ROUND_HEATMAP}
                      render={() => (
                        <MatchingRoundHeatmapPage
                          roundId={typedRoundId!}
                          tenantId={existingRound?.tenantId}
                          roundStatus={existingRound?.status}
                          refreshKey={heatmapRefreshKey}
                        />
                      )}
                    />
                    <Route
                      exact
                      path={Pages.MATCHING_ROUND_LOGS}
                      render={() => (
                        <MatchingRoundLogsPage roundId={typedRoundId!} />
                      )}
                    />
                  </>
                )}
                <Redirect
                  to={
                    isEditMode
                      ? getRoutePath(Pages.MATCHING_ROUND_DETAILS, {
                          roundId,
                        })
                      : Pages.NEW_MATCHING_ROUND
                  }
                />
              </Switch>
            </div>
            {showSimulate && location.pathname.endsWith('/subgroups') && (
              <div className={classes.footer}>
                <Button
                  onClick={handleSimulate}
                  variant='contained'
                  disabled={isSimulating || isSaving}
                  data-testid='button-simulate'
                >
                  {isSimulating ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Simulate'
                  )}
                </Button>
              </div>
            )}
            {showHeatmapActions && location.pathname.endsWith('/heatmap') && (
              <div className={classes.footer}>
                <Button
                  onClick={handleReview}
                  variant='contained'
                  disabled={isSendingReview || isSaving}
                  data-testid='action-review'
                >
                  {isSendingReview ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    '+ Review'
                  )}
                </Button>
              </div>
            )}
          </Paper>
        </>
      )}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title='Run New Simulation'
        body='Previous match recommendations and fixes will be lost if you run a new simulation round. Continue?'
        onCancel={() => setIsConfirmOpen(false)}
        onSuccess={handleConfirmSimulate}
        cancelProps={{ label: 'No', variant: 'outlined' }}
        successProps={{
          label: 'Yes',
          variant: 'contained',
          'data-testid': 'confirm-simulate-yes',
        }}
      />
      <ConfirmDialog
        isOpen={isReviewConfirmOpen}
        title='Send for Review'
        body={`This action will send emails to all Ventures and ${rb(
          'mentors-u',
        )} in this round to vote on proposed matches. Do you agree?`}
        onCancel={() => setIsReviewConfirmOpen(false)}
        onSuccess={handleConfirmReview}
        cancelProps={{ label: 'No', variant: 'outlined' }}
        successProps={{
          label: 'Yes',
          variant: 'contained',
          'data-testid': 'confirm-review-yes',
        }}
      />
      <ActionsDialog
        open={isReviewSuccessOpen}
        onClose={() => setIsReviewSuccessOpen(false)}
        title='Review Requested'
        actions={
          <Button
            onClick={() => setIsReviewSuccessOpen(false)}
            variant='contained'
            data-testid='confirm-review-ok'
          >
            Ok
          </Button>
        }
      >
        Emails will be sent out momentarily
      </ActionsDialog>
    </BaseLayout>
  );
}

export default MatchingRoundDetailsPage;
